import { Components as JoyComponents, Theme as JoyTheme } from '@mui/joy';
import JoyOption from '@mui/joy/Option';
import JoySelect from '@mui/joy/Select';
import { mergeProps, mergeRefs, useObjectRef } from '@react-aria/utils';
import clsx from 'clsx';
import { forwardRef, useRef, useState } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';

import { Description, Label } from './fieldset';
import {
  ComposedFormControllerProps,
  FormController,
  useFormController,
} from './form';
import { createSplitProps } from './utils';

export const Option = JoyOption;

export const overrides: JoyComponents<JoyTheme>['JoySelect'] = {
  defaultProps: { size: 'md', variant: 'outlined' },
  styleOverrides: {
    root: ({ ownerState, theme }) => ({
      ...(ownerState.size === 'lg' && {
        '--Select-minHeight': theme.spacing(6),
        fontSize: theme.fontSize.md,
        lineHeight: theme.lineHeight.lg,
      }),
    }),
  },
};

export type SelectProps = React.ComponentPropsWithoutRef<typeof JoySelect> & {
  controlled?: boolean;
  /**
   * Joy Components does not play well with Radix dialogs or popovers since it
   * disabled pointer events. this prop will change the mounting position
   * of the `options` to ba attached to the root wrapper of the Select
   */
  mountOptionsToRoot?: boolean;
};

export const Select = forwardRef<HTMLDivElement, SelectProps>(function Select(
  { controlled = true, mountOptionsToRoot = false, ...props },
  ref,
) {
  const controller = useFormController();
  const objectRef = useObjectRef(ref);
  const mergedRef = mergeRefs(ref, objectRef);
  const [open, setOpen] = useState(false);
  const skipRef = useRef(false);

  const containerRef = useRef<HTMLDivElement>(null);
  return (
    <JoySelect
      listboxOpen={open}
      onListboxOpenChange={(isOpen) => {
        if (!skipRef.current) {
          setOpen(isOpen);
        }
      }}
      data-slot='control'
      {...mergeProps(
        {
          placeholder: 'Select',
          /*
            issue: 
            options stay open when clicking outside
            ideal fix would be to update the package
            https://github.com/mui/material-ui/issues/39026

          */
          onBlur: (event) => {
            const selectElement = event.currentTarget;
            const newFocusElement =
              event.relatedTarget || document.activeElement;
            if (
              !skipRef.current &&
              !selectElement.contains(newFocusElement) &&
              !controlled
            ) {
              setOpen(false);
            }
          },
        } satisfies Partial<SelectProps>,
        props,
        controller && controlled
          ? ({
              ...controller?.field,
              onChange: (_e, value: unknown) =>
                controller?.field.onChange(value),
            } satisfies Partial<SelectProps>)
          : { ref: mergedRef },
        {
          ...(mountOptionsToRoot
            ? ({
                slotProps: {
                  root: { ref: containerRef },
                  listbox: {
                    popperOptions: { strategy: 'absolute' },
                    container: () => containerRef.current,
                  },
                },
              } satisfies Partial<SelectProps>)
            : {}),
        },
      )}
    />
  );
});

export function SelectField<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
>({
  label,
  description,
  children,
  ...props
}: ComposedFormControllerProps<T, TName> &
  Omit<SelectProps, keyof ComposedFormControllerProps<T, TName>> & {
    label?: React.ReactNode;
    description?: React.ReactNode;
  }) {
  const [controllerProps, controlProps] = createSplitProps<
    ComposedFormControllerProps<T, TName>
  >()(props, [
    'color',
    'color',
    'defaultValue',
    'disabled',
    'field',
    'control',
    'size',
    'required',
  ]);

  return (
    <FormController {...controllerProps}>
      <div
        className={clsx([
          '[&>[data-slot=label]+[data-slot=control]]:mt-2',
          '[&>[data-slot=label]+[data-slot=description]]:mt-1',
          '[&>[data-slot=description]+[data-slot=control]]:mt-2',
          '[&>[data-slot=control]+[data-slot=description]]:mt-2',
          '[&>[data-slot=control]+[data-slot=error]]:mt-2',
          '[&>[data-slot=label]]:font-medium',
        ])}
      >
        {label && <Label>{label}</Label>}
        <Select {...controlProps}>{children}</Select>
        {description && <Description>{description}</Description>}
      </div>
    </FormController>
  );
}
